// palette.jsx — the ⌘K command palette (advertised in the nav, now real).
// Searches sichos, concepts, sources, collections, the dynasty, and runs actions.
const { useState: useStateP, useEffect: useEffectP, useRef: useRefP, useMemo: useMemoP } = React;

// Build a flat, searchable index from the app's data + navigation actions.
function buildIndex(nav) {
  const idx = [];
  const push = (o) => idx.push(o);

  // pages / navigation
  [["search", "Search results", "Search with filters and share a results link", "search"],
   ["home", "Home", "Today’s sicha, Hayom Yom, discovery", "compass"],
   ["library", "Library", "Browse the corpus & the dynasty", "book"],
   ["explore", "Source map", "The full explorable source graph", "graph"],
   ["research", "Research workspace", "Build source sheets & shiur outlines", "layers"],
   ["topics", "Concepts", "Learn by idea", "path"]].filter(([k]) => k !== "topics" || window.topicsEnabled()).forEach(([k, title, sub, ic]) =>
    push({ group: "Go to", icon: ic, title, sub, keys: title + " " + sub, run: () => nav.go(k) }));

  // the live sicha + continue-learning
  push({ group: "Sichos", icon: "book", title: window.SICHA.titleEn, he: window.SICHA.titleHe,
    sub: `${window.SICHA.collection} · ${window.SICHA.volume.replace(/^.*· /, "")}`, keys: window.SICHA.titleEn + " " + window.SICHA.titleHe,
    run: () => nav.openReader() });
  (window.CONTINUE || []).filter((c) => !c.open).forEach((c) =>
    push({ group: "Sichos", icon: "book", title: c.en, he: c.he, sub: c.coll + " · " + Math.round(c.progress * 100) + "% read",
      keys: c.en + " " + c.he, run: () => nav.openReader(c.ref) }));

  // concepts
  (window.topicsEnabled() ? window.TOPICS || [] : []).forEach((tp) =>
    push({ group: "Concepts", icon: "path", title: tp.en, he: tp.he, sub: "Explore this concept",
      keys: tp.en + " " + tp.he, run: () => nav.openTopic(tp.slug || tp.id) }));

  // sources (mareh mekomos) + deeper map nodes — jump opens compare on that text
  const seen = new Set();
  ((window.MAP && window.MAP.nodes) || window.SOURCES || []).forEach((s) => {
    if (s.id === "center" || seen.has(s.id)) return; seen.add(s.id);
    push({ group: "Sources", icon: "quote", title: s.title, he: s.he, sub: `${s.citation} · ${s.type}${s.relationship ? " · " + s.relationship : ""}`,
      keys: s.title + " " + s.he + " " + s.citation + " " + s.type, dotType: s.type,
      run: () => nav.openCompare(s.id) });
  });

  // collections + dynasty
  (window.COLLECTIONS || []).forEach((c) =>
    push({ group: "Collections", icon: "layers", title: c.en, he: c.he, sub: `${c.author} · ${c.vols} vol · ${c.sources} sources`,
      keys: c.en + " " + c.he + " " + c.author, run: () => nav.go("library") }));
  (window.REBBEIM || []).forEach((r) =>
    push({ group: "The dynasty", icon: "compass", title: r.en, he: r.he, sub: `${r.full} · ${r.years}`,
      keys: r.en + " " + r.he + " " + r.full, run: () => nav.go("library") }));

  // actions
  [["Compare two texts", "Open the side-by-side compare view", "compare", () => nav.openCompare()],
   ["New source sheet", "Start a fresh sheet in Research", "plus", () => { window.WS.newSheet(); nav.go("research"); }],
   ["Start from this text’s sources", "Seed a sheet from the current piece’s quoted sources", "spark", () => { window.WS.seedCurrentSources(); nav.go("research"); }],
   ["Export source sheet (.doc)", "Download the active sheet for Word / Docs", "external", () => window.WS.exportDoc("sheet")],
   ["Print / Save as PDF", "Choose the text, language, and sources to print", "printer", () => window.dispatchEvent(new CustomEvent("mayanos-open-print"))]].forEach(([title, sub, ic, run]) =>
    push({ group: "Actions", icon: ic, title, sub, keys: title + " " + sub, run }));

  // display mode
  (window.DIRECTIONS || []).forEach((d) =>
    push({ group: "Display mode", icon: "sliders", title: d.label + " mode", sub: "Switch the visual style",
      keys: `${d.label} mode theme display ${d.id}`, run: () => nav.setDirection(d.id) }));

  return idx;
}

function scoreItem(it, q) {
  if (!q) return 0;
  const hay = (it.keys || (it.title + " " + (it.sub || ""))).toLowerCase();
  const t = it.title.toLowerCase();
  if (t === q) return 100;
  if (t.startsWith(q)) return 80;
  if (hay.includes(q)) return 50 - hay.indexOf(q) * 0.05;
  // loose subsequence
  let i = 0; for (const ch of hay) { if (ch === q[i]) i++; if (i === q.length) return 24; }
  return -1;
}

function CommandPalette({ open, onClose, nav }) {
  const [q, setQ] = useStateP("");
  const [active, setActive] = useStateP(0);
  const [liveHits, setLiveHits] = useStateP([]); // live results from the whole library
  const [liveLoading, setLiveLoading] = useStateP(false);
  const [liveError, setLiveError] = useStateP("");
  const [searchRetry, setSearchRetry] = useStateP(0);
  const inputRef = useRefP(null);
  const listRef = useRefP(null);
  const index = buildIndex(nav);

  // Live, library-wide search via the BFF (the whole MongoDB corpus). Selecting a
  // hit loads that real text into the reader.
  useEffectP(() => {
    const query = q.trim();
    if (query.length < 2) { setLiveHits([]); setLiveError(""); setLiveLoading(false); return; }
    let cancelled = false;
    const controller = new AbortController();
    setLiveLoading(true);
    setLiveError("");
    const id = setTimeout(async () => {
      try {
        const r = await fetch(`/bff/search?q=${encodeURIComponent(query)}`, { signal: controller.signal, headers: { Accept: "application/json" } });
        if (!r.ok) throw new Error("Search unavailable");
        const hits = await r.json();
        if (!cancelled) setLiveHits((hits || []).filter((h) => h.ref).slice(0, 12).map((h) => ({
          group: h.type === "text" ? "Text matches" : "Library", icon: h.type === "ref" ? "quote" : "book",
          title: h.title || h.ref, he: h.heTitle || "", sub: h.excerpt || ((h.type || "text") + " · open in reader"),
          keys: (h.title || "") + " " + (h.heTitle || "") + " " + (h.excerpt || ""),
          run: () => nav.openReader(h.ref),
        })));
      } catch (e) { if (!cancelled && e && e.name !== "AbortError") { setLiveHits([]); setLiveError("Search is temporarily unavailable."); } }
      finally { if (!cancelled) setLiveLoading(false); }
    }, 170);
    return () => { cancelled = true; clearTimeout(id); controller.abort(); };
  }, [q, searchRetry]);

  const results = useMemoP(() => {
    const query = q.trim().toLowerCase();
    if (!query) {
      // default: a curated launchpad
      const groups = ["Go to", "Sichos", "Concepts", "Actions"];
      return index.filter((it) => groups.includes(it.group)).slice(0, 9);
    }
    const local = index.map((it) => ({ it, s: scoreItem(it, query) }))
      .filter((x) => x.s > 0).sort((a, b) => b.s - a.s).slice(0, 12).map((x) => x.it);
    // live library hits first (the real corpus), then local nav/actions
    return liveHits.concat(local);
  }, [q, index, liveHits]);

  useEffectP(() => { setActive(0); }, [q, liveHits]);
  useEffectP(() => { if (open) { const initial = window.__mayanosPaletteQuery || ""; window.__mayanosPaletteQuery = ""; setQ(initial); setActive(0); setTimeout(() => inputRef.current && inputRef.current.focus(), 30); } }, [open]);
  useEffectP(() => {
    const el = listRef.current && listRef.current.querySelector(".cmd-row.on");
    if (el) el.scrollIntoView ? null : null; // avoid scrollIntoView per guidelines
    if (el && listRef.current) {
      const lr = listRef.current.getBoundingClientRect(), er = el.getBoundingClientRect();
      if (er.bottom > lr.bottom) listRef.current.scrollTop += er.bottom - lr.bottom + 8;
      else if (er.top < lr.top) listRef.current.scrollTop -= lr.top - er.top + 8;
    }
  }, [active]);

  if (!open) return null;
  const choose = (it) => { if (!it) return; onClose(); setTimeout(() => it.run(), 0); };
  const onKey = (e) => {
    if (e.key === "ArrowDown") { e.preventDefault(); setActive((a) => Math.min(results.length - 1, a + 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setActive((a) => Math.max(0, a - 1)); }
    else if (e.key === "Enter") { e.preventDefault(); choose(results[active]); }
    else if (e.key === "Escape") { e.preventDefault(); onClose(); }
  };

  // group the visible results, preserving order
  const grouped = [];
  results.forEach((it) => {
    let g = grouped.find((x) => x.group === it.group);
    if (!g) { g = { group: it.group, items: [] }; grouped.push(g); }
    g.items.push(it);
  });
  let flat = -1;

  return (
    <div className="cmd-scrim" onMouseDown={onClose}>
      <div className="cmd" role="dialog" aria-label="Command palette" onMouseDown={(e) => e.stopPropagation()}>
        <div className="cmd-input">
          <Icon d="search" size={19} style={{ color: "var(--text-3)", flex: "none" }} />
          <input ref={inputRef} value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={onKey}
            placeholder="Search sichos, concepts, sources — or type a command…" aria-label="Search" />
          <span className="kbd">esc</span>
        </div>
        <div className="cmd-list" ref={listRef}>
          {results.length === 0 && (
            <div className="cmd-empty">{liveLoading ? "Searching the library…" : liveError ? <><span>{liveError}</span><button className="mini-btn" onClick={() => setSearchRetry((value) => value + 1)}>Try again</button></> : <>No matches for “{q}”. Try a concept, a source, or “compare”.</>}</div>
          )}
          {grouped.map((g) => (
            <div key={g.group} className="cmd-group">
              <div className="cmd-group-h">{g.group}</div>
              {g.items.map((it) => {
                flat++; const idxNow = flat;
                return (
                  <button key={idxNow} className={"cmd-row" + (active === idxNow ? " on" : "")}
                    onMouseEnter={() => setActive(idxNow)} onClick={() => choose(it)}>
                    <span className="cmd-ic">{it.dotType ? <TypeDot type={it.dotType} size={9} /> : <Icon d={it.icon} size={17} />}</span>
                    <span className="cmd-txt">
                      <span className="cmd-title">{it.title}{it.he && <span lang="he" dir="rtl" className="cmd-he heb">{it.he}</span>}</span>
                      {it.sub && <span className="cmd-sub">{it.sub}</span>}
                    </span>
                    <span className="cmd-enter">{active === idxNow ? <Icon d="back" size={15} style={{ transform: "scaleX(-1) rotate(180deg)" }} /> : null}</span>
                  </button>
                );
              })}
            </div>
          ))}
        </div>
        <div className="cmd-foot">
          <span><span className="kbd">↑</span><span className="kbd">↓</span> navigate</span>
          <span><span className="kbd">↵</span> open</span>
          <span><span className="kbd">esc</span> close</span>
          <span className="cmd-foot-brand">Mayanos</span>
        </div>
      </div>
    </div>
  );
}

Object.assign(window, { CommandPalette });
